import { useQuery } from "@apollo/client";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Iconify from "src/components/iconify";
import { GET_CUSTOMER_PLAN } from "src/graphql/queries/floorplans";
import FloorplanAnimatedButton from "src/components/floormap/FloorplanAnimatedButton";
import { useTranslation } from "react-i18next";
import { PATHS } from "src/routes/paths";
import { useEffect, useMemo, useRef, useState } from "react";
import FloorplanAnimatedTable from "src/components/floormap/FloorplanAnimatedTable";
import ReservationDetailsCard from "src/components/floormap/ReservationDetailsCard";
import { PrimaryButton, SecondaryButton } from "../listing-details/listingButtons";
import dayjs from "dayjs";
import ReactGA from "react-ga4";
import { useSnackbar } from "notistack";
import { useMutationWithAuth } from "src/graphql/instances";
import { CREATE_BOOKING_HOLDING_FEE_USER } from "src/graphql/mutations/bookings";
import MoveIcon from "src/assets/icons/booking/MoveIcon";
import { Layer, Stage, Image as KonvaImage } from "react-konva";
import useResponsive from "src/hooks/useResponsive";
import arrowLeftImg from "src/assets/floormap-icon-images/left-arrow.png";
import arrowRightImg from "src/assets/floormap-icon-images/right-arrow.png";
import checkIcon from 'src/assets/floormap-icon-images/check.png';
import busyIcon from 'src/assets/floormap-icon-images/busy.png';
import CirleCheckWhiteIcon from "src/assets/icons/floor-plan/CircleCheckWhite";
import EventImageLayout from "src/layouts/event";
import MobileIconLarge from "src/assets/icons/floor-plan/MobileIconLarge";
import { replace } from "stylis";
import { CLEAR_CART_ITEMS } from "src/graphql/mutations";

const maxScale = 2.5;

export default function BookingFloorMap() {
    const { bookingId } = useParams();
    const token = localStorage.getItem("accessToken");
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const isMobile = useResponsive("down", "sm");
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const params = new URLSearchParams(window.location.search);
    const embedMode = params.get("embed") === "true";
    const isEmbedded = embedMode || window.self !== window.top;
    const queryParam = (isEmbedded ? '?embed=true' : '');


    const [createBookingHoldingFee] = useMutationWithAuth(
        CREATE_BOOKING_HOLDING_FEE_USER
    );

    const {
        eventId,
        isReservationsOnly,
        selectedArea,
        formData,
        activeStep,
        availableTables,
        holdingFee,
        minTables,
        eventData,
        selectedTickets,
        selectedMenus
    } = location.state || null;

    const { data: floorPlanRes } = useQuery(
        GET_CUSTOMER_PLAN,
        {
            context: {
                headers: {
                    "x-nightz-standard-behaviour": "true",
                    "x-nightz-platform": "web",
                    Authorization: token,
                },
            },
            variables: { bookingModuleId: bookingId },
            skip: !bookingId
        }
    );

    const [clearCartItems] = useMutationWithAuth(CLEAR_CART_ITEMS);

    const [tables, setTables] = useState([]);
    const [selectedTables, setSelectedTables] = useState([]);
    const [showOverlay, setShowOverlay] = useState(true);
    const [fadeOut, setFadeOut] = useState(false);
    const [areaButtons, setAreaButtons] = useState([]);
    const [imgShape, setImgShape] = useState({ width: 0, height: 0, src: '' });
    const [screenScale, setScreenScale] = useState(0);
    const [minScale, setMinScale] = useState(0);
    const [dragEnabled, setDragEnabled] = useState(true);
    const [icons, setIcons] = useState({ arrowLeft: null, arrowRight: null, check: null, busy: null });
    const [stageScale, setStageScale] = useState(1);
    const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
    const [image, setImage] = useState(null);
    const [initialSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    const stageRef = useRef();
    const containerRef = useRef();
    const pinchStartScaleRef = useRef(null);
    const pinchStartDistRef = useRef(null);
    const pinchStartMidpointRef = useRef(null);
    const animatablesRef = useRef([]);

    const nrSelectedTables = useMemo(() => {
        return selectedTables.length;
    }, [selectedTables]);

    const nrSelectedSeats = useMemo(() => {
        return selectedTables.reduce((sum, table) => sum + table.capacity, 0);
    }, [selectedTables]);

    const isSelectionComplete = useMemo(() => {
        return nrSelectedTables === minTables && nrSelectedSeats >= formData.guests;
    }, [nrSelectedTables, nrSelectedSeats]);


    useEffect(() => {
        const planData = floorPlanRes?.floorPlan?.customerPlanData;
        if (!planData) return;
        const floorPlan = planData?.[selectedArea.value];
        const oldImgShapeWithPos = floorPlan?.find((shape) => shape.type === "image");
        const oldImgPos = { x: oldImgShapeWithPos?.x, y: oldImgShapeWithPos?.y }
        const oldImgShape = { ...oldImgShapeWithPos, x: 0, y: 0 }

        const viewport = floorPlan
            ?.filter((shape) => shape.type === "viewport" && shape.id.includes(isMobile ? "mobile" : "desktop"))
            ?.map((shape) => {
                return {
                    x: shape.x - oldImgPos?.x,
                    y: shape.y - oldImgPos?.y,
                    ...(isMobile ? { height: shape.height } : { width: shape.width })
                }
            })[0] || { x: 0, y: 0, height: 400 };


        const areaTables = floorPlan?.filter((shape) => shape.type === "table")

        const screenScale = isMobile ? window.innerHeight / viewport.height : window.innerWidth / viewport.width;
        const imgShape = { width: oldImgShape.width * screenScale, height: oldImgShape.height * screenScale, src: oldImgShape.src }

        setStagePos({ x: -screenScale * viewport.x, y: -screenScale * viewport.y })

        const minScale = Math.max(
            window.innerWidth / imgShape.width,
            window.innerHeight / imgShape.height
        );

        if (areaTables?.length && availableTables?.length) {
            let tablesWithStatus = areaTables.map((table) => {
                return {
                    ...table,
                    status: availableTables.some((t) => t.id === table.id) ? "available" : "busy",
                    x: (table.x - oldImgPos.x + 12) * screenScale,
                    y: (table.y - oldImgPos.y + 12) * screenScale
                }
            });
            if (minTables === 1) {
                const capacities = [...new Set(tablesWithStatus.filter(t => t.status === "available").map(t => t.capacity))];
                const minCapacity = Math.min(...capacities.filter(c => c >= formData.guests));
                tablesWithStatus = tablesWithStatus.filter(t => t.capacity === minCapacity || t.status === "busy");

            }
            setTables(tablesWithStatus);
        }

        const areaButtons = floorPlan
            ?.filter((shape) =>
                shape.type === "button" &&
                (isReservationsOnly
                    ? shape.name !== "Welcome"
                    : selectedArea.value === "Entrance" ?
                        shape.name === "Welcome" :
                        shape.name !== "Entrance"
                )
            )
            .map((shape) => {
                return {
                    ...shape,
                    x: (shape.x - oldImgPos?.x) * screenScale,
                    y: (shape.y - oldImgPos?.y) * screenScale
                }
            });

        setAreaButtons(areaButtons);
        setImgShape(imgShape);
        setScreenScale(screenScale);
        setMinScale(minScale);
    }, [floorPlanRes]);

    const registerAnimation = (updateFn) => {
        animatablesRef.current.push(updateFn);
    };

    useEffect(() => {
        if (!stageRef.current) return;
        const layer = stageRef.current.findOne(".buttonsLayer");
        const anim = new window.Konva.Animation((frame) => {
            if (isIOS && frame.time % 2 > 0) return;
            animatablesRef.current.forEach((updateFn) => updateFn(frame));
        }, layer);

        anim.start();
        return () => anim.stop();
    }, [tables]);

    useEffect(() => {
        const loadImage = (src) => new Promise((res) => {
            const img = new window.Image();
            img.src = src;
            img.onload = () => res(img);
        });

        Promise.all([
            loadImage(arrowLeftImg),
            loadImage(arrowRightImg),
            loadImage(checkIcon),
            loadImage(busyIcon),
        ]).then(([arrowLeft, arrowRight, check, busy]) => {
            setIcons({ arrowLeft, arrowRight, check, busy });
        });
    }, []);

    useEffect(() => {
        const restoreDrag = () => setDragEnabled(true);
        window.addEventListener('touchend', restoreDrag, { passive: true });
        window.addEventListener('touchcancel', restoreDrag, { passive: true });
        return () => {
            window.removeEventListener('touchend', restoreDrag);
            window.removeEventListener('touchcancel', restoreDrag);
        };
    }, []);

    useEffect(() => {
        const i = new window.Image();
        i.src = imgShape.src;
        i.onload = () => setImage(i);
    }, [imgShape.src]);

    useEffect(() => {
        if (selectedArea?.value === "Entrance" && imgShape.src) {
            const fadeTimer = setTimeout(() => setFadeOut(true), 500);
            const removeTimer = setTimeout(() => {
                setShowOverlay(false);
                setFadeOut(false);
            }, 1500);

            return () => {
                clearTimeout(fadeTimer);
                clearTimeout(removeTimer);
            };
        }
    }, [imgShape.src]);

    useEffect(() => {
        const preventBrowserZoom = (e) => {
            if (e.ctrlKey) {
                e.preventDefault();
            }
        };

        window.addEventListener("wheel", preventBrowserZoom, { passive: false });

        return () => {
            window.removeEventListener("wheel", preventBrowserZoom);
        };
    }, []);

    const clampPosition = (pos, scale) => {
        const imgWidth = imgShape.width * scale;
        const imgHeight = imgShape.height * scale;

        const minX = window.innerWidth - imgWidth;
        const maxX = 0;
        const minY = window.innerHeight - imgHeight;
        const maxY = 0;

        return {
            x: Math.max(minX, Math.min(maxX, pos.x)),
            y: Math.max(minY, Math.min(maxY, pos.y)),
        };
    };

    const handleWheel = (e) => {
        if (!e.evt.ctrlKey) return;
        e.evt.preventDefault();

        const stage = stageRef.current;
        const oldScale = stage.scaleX();
        const pointer = stage.getPointerPosition();

        const mousePointTo = {
            x: (pointer.x - stage.x()) / oldScale,
            y: (pointer.y - stage.y()) / oldScale,
        };

        const scaleBy = 1.1;
        const direction = e.evt.deltaY > 0 ? -1 : 1;

        let newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

        const maxScale = 2.5;

        newScale = Math.max(minScale, Math.min(maxScale, newScale));

        const newPos = {
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale,
        };

        const clampedPos = clampPosition(newPos, newScale);

        setStageScale(newScale);
        setStagePos(clampedPos);
    };

    const getTouchDistance = (touch1, touch2) => {
        return Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
    };

    const getTouchMidpoint = (touch1, touch2) => {
        return {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2
        };
    };

    const handleTouchStart = (e) => {
        if (e.evt.touches.length === 2) {
            e.evt.preventDefault();
            setDragEnabled(false);
            const [t1, t2] = e.evt.touches;

            const stage = stageRef.current;
            pinchStartScaleRef.current = stage.scaleX();
            pinchStartDistRef.current = getTouchDistance(t1, t2);
            pinchStartMidpointRef.current = getTouchMidpoint(t1, t2);
        }
    };

    const handleTouchMove = (e) => {
        if (e.evt.touches.length !== 2 || pinchStartDistRef.current === null) return;

        e.evt.preventDefault();
        const [t1, t2] = e.evt.touches;
        const stage = stageRef.current;

        const dist = getTouchDistance(t1, t2);
        const currentMidpoint = getTouchMidpoint(t1, t2);

        let newScale = pinchStartScaleRef.current * (dist / pinchStartDistRef.current);

        newScale = Math.max(minScale, Math.min(maxScale, newScale));

        const pointTo = {
            x: (currentMidpoint.x - stage.x()) / stage.scaleX(),
            y: (currentMidpoint.y - stage.y()) / stage.scaleY(),
        };

        const newPos = {
            x: currentMidpoint.x - pointTo.x * newScale,
            y: currentMidpoint.y - pointTo.y * newScale
        };

        const clampedPos = clampPosition(newPos, newScale);

        setStageScale(newScale);
        setStagePos(clampedPos);
    };

    const handleTouchEnd = (e) => {
        if (e.evt.touches.length < 2) {
            pinchStartScaleRef.current = null;
            pinchStartDistRef.current = null;
            pinchStartMidpointRef.current = null;
            const stage = stageRef.current;
            requestAnimationFrame(() => setDragEnabled(true));
        }
    };


    const handleBack = () => {
        selectedArea.label === "Entrance" ? navigate(-1) : handleModify();
    }

    const handleSelectArea = (buttonId) => {
        const areaClicked = buttonId.split("-")[1];
        if (areaClicked === "Entrance") {
            navigate(PATHS.booking.floorMap(bookingId) + queryParam, {
                state: {
                    eventId: eventData?.id,
                    isReservationsOnly: isReservationsOnly,
                    selectedArea: { label: "Entrance", value: "Entrance" },
                }, replace: true
            })
            return;
        };
        isReservationsOnly
            ? navigate(PATHS.booking.new(eventId) + queryParam, {
                state: {
                    selectedArea: areaClicked,
                },
                replace: true
            })
            : navigate(PATHS.booking.newWizard(eventId) + queryParam, {
                state: {
                    selectedTickets: selectedTickets,
                    selectedMenus: selectedMenus,
                    ...(selectedArea.value !== "Entrance" ? { showBooking: areaClicked } : {}),
                },
                replace: true
            })
    }

    const handleClickTable = (id) => {
        const clickedTable = tables.find(t => t.id === id);
        setTables(prevTables =>
            prevTables.map(table => {
                if (table.id !== id) return table;
                if (table.status === 'busy') return table;
                const newStatus = table.status === 'available' ? 'selected' : 'available';
                return { ...table, status: newStatus };
            })
        );
        if (clickedTable.status === 'selected') {
            setSelectedTables(prev => prev.filter(t => t.id !== clickedTable.id));
            return;
        }
        if (clickedTable.status === 'available') {
            if (selectedTables.length === minTables) {
                const tableToRemove = selectedTables[0];
                setTables(prevTables =>
                    prevTables.map(table => {
                        if (table.id !== tableToRemove.id) return table;
                        return { ...table, status: 'available' };
                    })
                );
                setSelectedTables(prev => [...prev.slice(1), clickedTable])
            } else {
                setSelectedTables(prev => [...prev, clickedTable])
            }
        }
    }

    const handleModify = () => {
        isReservationsOnly
            ? navigate(PATHS.booking.new(eventId) + queryParam, {
                state: {
                    formData: formData,
                    activeStep: activeStep,
                },
                replace: true
            })
            : navigate(PATHS.booking.newWizard(eventId) + queryParam, {
                state: {
                    selectedTickets: selectedTickets,
                    selectedMenus: selectedMenus,
                    showBooking: formData.area,
                    selectedBookingConfig: {
                        bookingModuleId: bookingId,
                        numberOfGuests: formData.guests,
                        bookedDate: formData.date ?? dayjs(eventData?.startDate).format("YYYY-MM-DD"),
                        duration: formData.length,
                        areaId: formData.area,
                        bookedHour: formData.time,
                        holdingFee,
                    }
                },
                replace: true
            })
    }

    const handleContinue = () => {
        const tableIds = tables.filter((t) => t.status === "selected").map(t => t.id);
        if (isReservationsOnly) {
            if (activeStep >= 0) {
                holdingFee
                    ? handleCreateBookingReservationWithFee(tableIds)
                    : navigate(PATHS.booking.new(eventId) + queryParam, {
                        state: {
                            formData: { ...formData, tables: tableIds },
                            activeStep: activeStep + 1
                        },
                        replace: true
                    })
            }
        } else {
            navigate(PATHS.booking.newWizard(eventId) + queryParam, {
                state: {
                    selectedTickets: selectedTickets,
                    selectedMenus: selectedMenus,
                    selectedBookingConfig: {
                        bookingModuleId: bookingId,
                        numberOfGuests: formData.guests,
                        bookedDate: formData.date ?? dayjs(eventData?.startDate).format("YYYY-MM-DD"),
                        duration: formData.length,
                        areaId: formData.area,
                        bookedHour: formData.time,
                        tableIds: tableIds,
                        holdingFee,
                    }
                },
                replace: true
            })
        }
    }

    const handleCreateBookingReservationWithFee = async (tableIds) => {
        try {
            let newBookingData = {
                bookedDate: formData.date ?? dayjs(eventData?.startDate).format("YYYY-MM-DD"),
                bookedHour: formData.time ?? dayjs(eventData?.startDate).format("HH:mm"),
                numberOfGuests: formData.guests,
                duration: formData.length,
                bookingModuleId: bookingId,
                areaId: formData.area,
                tableIds: tableIds
            };

            await clearCartItems();
            const bookingResult = await createBookingHoldingFee({
                variables: {
                    metadata: { booking: { ...newBookingData, holdingFee } },
                },
            });

            if (typeof bookingResult?.data?.createCartBookingHoldingFee?.id === "string") {
                ReactGA.event("add_to_cart", {
                    currency: eventData?.currency,
                    value: holdingFee,
                    item_list_id: eventData?.id,
                    item_list_name: eventData?.name,
                    items: [{
                        item_id: newBookingData.areaId,
                        item_name: selectedArea.label,
                        item_list_id: eventData?.id,
                        item_list_name: eventData?.id,
                        price: holdingFee,
                        quantity: 1
                    }]
                });
                navigate(PATHS.listings.checkout + queryParam);

            }
        } catch (err) {
            console.log(err)
            enqueueSnackbar(t("bookings.noAvailibityError"), {
                variant: "error",
                autoHideDuration: 5000,
            });
        }
    };

    const continueRandomAllocation = () => {
        isReservationsOnly
            ? navigate(PATHS.booking.new(eventData?.id) + queryParam, { replace: true })
            : navigate(PATHS.booking.newWizard(eventData?.id) + queryParam, { state: { showBooking: formData.area }, replace: true })
    }

    if (!imgShape.src) return <></>;

    if (isMobile && selectedArea.value === "Entrance") return (
        <Box
            sx={{
                width: "100vw",
                height: "100vh",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 0,
                }}
            >
                <img
                    alt="background"
                    src={eventData?.images[0]}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: "blur(30px) brightness(40%)",
                    }}
                />
            </Box>

            <Box
                onClick={handleBack}
                sx={{
                    position: "absolute",
                    top: 30,
                    left: "5%",
                    color: "white",
                    zIndex: 2,
                    cursor: "pointer",
                }}
            >
                <Iconify icon="material-symbols:chevron-left" width={24} />
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "100%",
                    flex: 1,
                    zIndex: 1,
                    position: "relative",
                    padding: 2,
                    color: "white",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        justifyContent: "center",
                        alignItems: "center",
                        marginX: 2,
                        flex: 1
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                            alignItems: "flex-end",
                            marginX: 2,
                        }}
                    >
                        <Typography variant="body2"><MobileIconLarge /></Typography>
                        <Box>
                            <Typography variant="h5" color="white">
                                {t("bookings.bookTableYouWant1")}
                            </Typography>
                            <Typography variant="h5" color="white" sx={{ mt: "-2px", pb: "3px" }}>
                                {t("bookings.bookTableYouWant2")}
                            </Typography>
                        </Box>

                    </Box>
                    <Box
                        sx={{
                            width: "calc(100vw - 32px)",
                            aspectRatio: "1 / 1",
                            maxWidth: "600px",
                            overflow: "hidden",
                            borderRadius: 1,
                        }}
                    >
                        <img
                            src={imgShape.src}
                            alt="venue"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center",
                                display: "block",
                            }}
                        />
                    </Box>
                    <Typography variant="body2" color="white" align="center">{t("bookings.exploreTheVenue")}</Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", marginX: 2, marginBottom: 1, gap: 1.5 }}>
                    <PrimaryButton
                        variant="large"
                        sx={{ height: "48px", width: "100%", textTransform: "none" }}
                        onClick={() => {
                            window.location.href = `https://nightz.app/event/${eventData?.id}`;
                        }}
                    >
                        {t("bookings.chooseYourTable")}
                    </PrimaryButton>
                    <SecondaryButton
                        variant="large"
                        sx={{ height: "48px", width: "100%", backgroundColor: "transparent", textTransform: "none" }}
                        onClick={continueRandomAllocation}
                    >
                        {t("bookings.randomAllocation")}
                    </SecondaryButton>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box
            ref={containerRef}
            sx={{
                width: "100dvw",
                height: "100dvh",
                position: "relative",
                overflow: "hidden",
                bgcolor: "black",
            }}
        >
            <Stage
                ref={stageRef}
                style={{
                    transform: `scale(${Math.min(
                        window.innerWidth / initialSize.width,
                        window.innerHeight / initialSize.height
                    )})`,
                    transformOrigin: "top left",
                }}
                width={initialSize.width}
                height={initialSize.height}
                scaleX={stageScale}
                scaleY={stageScale}
                x={stagePos.x}
                y={stagePos.y}
                draggable={dragEnabled}
                pixelRatio={isIOS ? 1 : undefined}
                onDragMove={(e) => {
                    const clamped = clampPosition(e.target.position(), stageScale);
                    e.target.position(clamped);
                    setStagePos(clamped);
                }}
                onWheel={handleWheel}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <Layer>
                    {image && (
                        <KonvaImage image={image} width={imgShape.width} height={imgShape.height} listening={false} />
                    )}
                </Layer>
                <Layer name="buttonsLayer">
                    {selectedArea.value !== "Entrance" && tables?.map((table, i) => {
                        return (<FloorplanAnimatedTable
                            key={i}
                            status={table.status}
                            x={table.x}
                            y={table.y}
                            radius={Math.floor(12 * screenScale)}
                            checkImg={icons.check}
                            busyImg={icons.busy}
                            registerAnimation={registerAnimation}
                            onClick={() => handleClickTable(table.id)}
                        />)
                    })}
                    {areaButtons?.map((btn, i) => {
                        return (<FloorplanAnimatedButton
                            key={i}
                            direction={btn.direction}
                            label={btn.name}
                            x={btn.x}
                            y={btn.y}
                            size={Math.floor(48 * screenScale)}
                            fontSize={Math.floor(24 * screenScale)}
                            isMobile={isMobile}
                            arrowImage={btn.direction === "right" ? icons.arrowRight : icons.arrowLeft}
                            onClick={() => handleSelectArea(btn.id)}
                        />)
                    })}
                </Layer>
            </Stage>
            {isMobile && <Box
                onClick={handleBack}
                sx={{
                    color: "white",
                    zIndex: 13,
                    position: "absolute",
                    top: 30,
                    left: "5%",
                }}
            >
                <Iconify icon="material-symbols:chevron-left" width={40} />
            </Box>}
            {!isMobile && <Button
                onClick={handleBack}
                sx={{
                    color: "white",
                    zIndex: 13,
                    position: "absolute",
                    top: 30,
                    left: "5%",
                }}
            >
                <Iconify icon="material-symbols:chevron-left" width={24} />
                {t('productPage.back')}
            </Button>}
            {selectedArea.value !== "Entrance" &&
                <ReservationDetailsCard
                    formData={{ ...formData, area: selectedArea.label }}
                    handleModify={handleModify}
                />
            }
            {selectedArea.value !== "Entrance" &&
                <Box sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    width: "100dvw",
                    height: "100px",
                    backdropFilter: "blur(10px)",
                    color: "#fff",
                    textAlign: "center",
                    lineHeight: "65px",
                    zIndex: 100,
                }}>
                    <Box sx={{
                        width: "280px",
                        marginTop: "auto",
                        marginBottom: "auto",
                        marginLeft: "auto",
                        marginRight: "auto",
                    }}>
                        <Box display="flex" width="100%" justifyContent="space-between" sx={{ mt: 0.5 }}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="h6" textAlign="center" alignContent="center" textTransform="capitalize">
                                    {`${t('bookings.tables')} (${nrSelectedTables}/${minTables})`}
                                </Typography>
                                {nrSelectedTables === minTables && <CirleCheckWhiteIcon />}
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="h6" textAlign="center" alignContent="center">
                                    {`${t('checkout.seat')} (${nrSelectedSeats}/${formData.guests})`}
                                </Typography>
                                {nrSelectedSeats >= formData.guests ? <CirleCheckWhiteIcon /> : <Box width="24px"></Box>}
                            </Box>
                        </Box>
                        <PrimaryButton
                            size="large"
                            variant="contained"
                            disabled={!isSelectionComplete}
                            sx={{
                                width: "100%",
                                textTransform: "none",
                                backgroundColor: "white",
                                color: "black",
                                "&:hover": {
                                    backgroundColor: "white",
                                    color: "black",
                                }
                            }}
                            onClick={handleContinue}
                        >
                            {t('bookings.continue')}
                        </PrimaryButton>
                    </Box>
                </Box>
            }

            {showOverlay && selectedArea.value === "Entrance" && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100dvw",
                        height: "100dvh",
                        bgcolor: "rgba(0,0,0,0.7)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        zIndex: 11,
                        opacity: fadeOut ? 0 : 1,
                        transition: "opacity 1s ease-out",
                    }}
                >
                    <MoveIcon />
                    <Typography variant="h6" color="white" sx={{ mt: 2 }}>
                        {t('bookings.dragToMove')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
